import { inject } from '@angular/core';
import { ResolveFn, ActivatedRouteSnapshot, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { EncryptionService } from '../services/encryption.service';
import { AuthService } from '../services/auth.service';
import { TenantService } from '../services/tenant.service';
import { Tenant } from '../models/tenant.model';

export interface ForgotPasswordLinkData {
  guid: string;
  patientUID: string;
  tenantCode: string;
  expiryTime: string;
}

export const forgotPasswordLinkResolver: ResolveFn<ForgotPasswordLinkData | null> = async (
  route: ActivatedRouteSnapshot
) => {
  const router = inject(Router);
  const encryptionService = inject(EncryptionService);
  const authService = inject(AuthService);
  const tenantService = inject(TenantService);

  try {
    // Get tenant config from previous resolver or fetch it if not available
    let tenantConfig = route.data['tenantConfig'] as Tenant | null;
    
    // If not available from resolver (resolvers run in parallel), fetch it
    if (!tenantConfig) {
      const tenantShortName = route.paramMap.get('tenantShortName');
      if (!tenantShortName) {
        router.navigate(['/page-not-found'], { replaceUrl: true });
        return null;
      }
      
      // Check if current tenant already matches (avoid reloading)
      const currentTenant = tenantService.getCurrentTenant();
      if (currentTenant && currentTenant.tenantShortName === tenantShortName) {
        tenantConfig = currentTenant;
      } else {
        // Find tenant by short name
        tenantConfig = await firstValueFrom(tenantService.getTenantByShortName(tenantShortName));
        if (tenantConfig) {
          tenantService.setCurrentTenant(tenantConfig);
        }
      }
    }
    
    if (!tenantConfig) {
      router.navigate(['/page-not-found'], { replaceUrl: true });
      return null;
    }

    // Get the encrypted Id parameter from query string
    const queryParams = route.queryParams;
    const encryptedId = queryParams['Id'] || queryParams['id'] || '';
    
    // If Id parameter is missing, redirect to page-not-found
    if (!encryptedId) {
      router.navigate(['/page-not-found'], { replaceUrl: true });
      return null;
    }

    // PE-6337 — Decrypt the Id parameter (AES-GCM async)
    let decryptedData: string;
    try {
      decryptedData = await encryptionService.decryptAes256Gcm(encryptedId);
    } catch (error) {
      router.navigate(['/page-not-found'], { replaceUrl: true });
      return null;
    }
    
    // Parse the decrypted data
    const parsedData = parseDecryptedData(decryptedData);
    
    if (!parsedData || !parsedData.guid || !parsedData.patientid || !parsedData.expiryTime || !parsedData.tenantcode) {
      router.navigate(['/page-not-found'], { replaceUrl: true });
      return null;
    }

    // Validate tenantShortName from URL matches resolved tenant
    const tenantShortNameFromUrl = route.paramMap.get('tenantShortName');
    if (!tenantShortNameFromUrl || tenantShortNameFromUrl !== tenantConfig.tenantShortName) {
      router.navigate(['/page-not-found'], { replaceUrl: true });
      return null;
    }

    // Validate tenantcode from link matches tenantCode from resolved tenant
    const linkTenantCode = parsedData.tenantcode.trim();
    const resolvedTenantCode = tenantConfig.tenantCode.trim();
    const tenantCodeMatches = resolvedTenantCode.startsWith(linkTenantCode) || resolvedTenantCode === linkTenantCode;
    
    if (!tenantCodeMatches) {
      router.navigate(['/page-not-found'], { replaceUrl: true });
      return null;
    }

    // Call checklinkstatus API first
    let linkStatusResponse: any;
    try {
      linkStatusResponse = await firstValueFrom(
        authService.checkLinkStatus(parsedData.guid, parsedData.patientid, parsedData.tenantcode)
      );
    } catch (error) {
      router.navigate(['/page-not-found'], { replaceUrl: true });
      return null;
    }

    // Check if password was already updated
    const isPasswordUpdated = linkStatusResponse?.data?.ispasswordupdated;
    if (isPasswordUpdated === true) {
      router.navigate(['/page-not-found'], { replaceUrl: true });
      return null;
    }

    // Call get-practice-time API to get current time
    let practiceTimeResponse: any;
    try {
      practiceTimeResponse = await firstValueFrom(authService.getPracticeTime(parsedData.tenantcode));
    } catch (error) {
      router.navigate(['/page-not-found'], { replaceUrl: true });
      return null;
    }

    // Extract GlobalDate from response
    const globalDate = practiceTimeResponse?.data?.[0]?.GlobalDate;
    if (!globalDate) {
      router.navigate(['/page-not-found'], { replaceUrl: true });
      return null;
    }

    // Compare expiry time with practice time
    if (!isExpiryDateValid(parsedData.expiryTime, globalDate)) {
      router.navigate(['/page-not-found'], { replaceUrl: true });
      return null;
    }

    // All validations passed, return the parsed data
    return {
      guid: parsedData.guid,
      patientUID: parsedData.patientid,
      tenantCode: parsedData.tenantcode,
      expiryTime: parsedData.expiryTime
    };
  } catch (error) {
    console.error('Error in forgot password link resolver:', error);
    router.navigate(['/page-not-found'], { replaceUrl: true });
    return null;
  }
};

/**
 * Parse decrypted data - format: guid,patientid,expiry time,tenantcode
 * Example: a372b1e0-0d69-4c9b-ad1f-6396eef2d147,204630747693156089,23-12-2025 04:08:11,18fee996af48681c
 * Note: Expiry time format is "DD-MM-YYYY HH:mm:ss" which contains a space, not commas
 */
function parseDecryptedData(decryptedData: string): { guid: string; patientid: string; expiryTime: string; tenantcode: string } | null {
  if (!decryptedData) {
    return null;
  }

  // Split by comma - format is: guid,patientid,expiry time,tenantcode
  // Expiry time is "DD-MM-YYYY HH:mm:ss" which doesn't contain commas
  const parts = decryptedData.split(',');
  
  if (parts.length !== 4) {
    return null;
  }

  return {
    guid: parts[0].trim(),
    patientid: parts[1].trim(),
    expiryTime: parts[2].trim(), // Format: "DD-MM-YYYY HH:mm:ss"
    tenantcode: parts[3].trim()
  };
}

/**
 * Validate if the expiry date is still valid (not expired)
 * Compares expiryTimeString (format: "DD-MM-YYYY HH:mm:ss") with globalDate (ISO format: "2025-12-26T13:41:07.86")
 */
function isExpiryDateValid(expiryTimeString: string, globalDate: string): boolean {
  if (!expiryTimeString || !expiryTimeString.trim() || !globalDate) {
    return false;
  }

  try {
    // Parse expiry date format: "DD-MM-YYYY HH:mm:ss"
    const trimmedExpiry = expiryTimeString.trim();
    const [datePart, timePart] = trimmedExpiry.split(' ');
    if (!datePart || !timePart) {
      return false;
    }

    // Parse date: DD-MM-YYYY
    const dateParts = datePart.split('-');
    if (dateParts.length !== 3) {
      return false;
    }

    const day = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1; // Month is 0-indexed in Date
    const year = parseInt(dateParts[2], 10);

    // Parse time: HH:mm:ss
    const timeParts = timePart.split(':');
    if (timeParts.length < 2) {
      return false;
    }

    const hour = parseInt(timeParts[0], 10);
    const minute = parseInt(timeParts[1], 10);
    const second = timeParts.length > 2 ? parseInt(timeParts[2], 10) : 0;

    if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(hour) || isNaN(minute)) {
      return false;
    }

    const expiryDate = new Date(year, month, day, hour, minute, second);

    // Parse globalDate (ISO format: "2025-12-26T13:41:07.86")
    const currentDate = new Date(globalDate);

    if (isNaN(expiryDate.getTime()) || isNaN(currentDate.getTime())) {
      return false;
    }

    // Check if expiry date is still valid (expiryDate >= currentDate)
    return expiryDate >= currentDate;
  } catch (error) {
    return false;
  }
}

